import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import { generateQueryParams } from "../../utils/generateQueryParams";

/**
 * params can include:
 *  page, limit, search, status, tier, serviceType
 *  -> all map to your backend query string
 */
export const fetchAdminUserSubscriptions = createAsyncThunk(
  "userSubscriptions/fetchAdminUserSubscriptions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(
        "/admin/subscriptions/user-subscriptions",
        { params }
      );

      // Backend shape: { status, data: [...], meta: {...} }
      return {
        subscriptions: response.data.data || [],
        meta: response.data.meta || {},
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to fetch user subscriptions",
        }
      );
    }
  }
);

export const suspendAdminUserSubscription = createAsyncThunk(
  "userSubscriptions/suspendAdminUserSubscription",
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/subscriptions/admin/user-subscriptions/${subscriptionId}/suspend`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to suspend user subscription",
        }
      );
    }
  }
);

export const reactivateAdminUserSubscription = createAsyncThunk(
  "userSubscriptions/reactivateAdminUserSubscription",
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/subscriptions/admin/user-subscriptions/${subscriptionId}/reactivate`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// -------- USER SUBSCRIPTION PLAN (PROJECT) THUNKS --------

// Get subscription details by ID
export const getSubscriptionById = createAsyncThunk(
  "subscription/getSubscriptionById",
  async (data) => {
    const response = await api.get(
      `/admin/subscriptions/active/${data}`
    );
    return response.data;
  }
);

// ========== ADMIN PROJECT TEAM MANAGEMENT ==========

/**
 * Get available admin team members for project
 */
export const getAvailableAdminForSubProjectTeamMembers = createAsyncThunk(
  "adminSubscriptions/getAvailableAdminTeamMembers",
  async (projectId) => {
    const response = await api.get(
      `/admin/subscriptions/projects/${projectId}/available-admin-members`
    );
    return response.data.data;
  }
);

/**
 * Add admin team member to project
 */
export const addAdminTeamMemberForSubProject = createAsyncThunk(
  "adminSubscriptions/addAdminTeamMember", 
  async ({ projectId, memberId, role, permissionType }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/${projectId}/admin-members`,
      { memberId, role, permissionType }
    );
    return response.data.data;
  }
);

/**
 * Remove admin team member from project
 */
export const removeAdminTeamMemberFromSubProject = createAsyncThunk(
  "adminSubscriptions/removeAdminTeamMember",
  async ({ projectId, userId }) => {
    const response = await api.delete(
      `/admin/subscriptions/projects/${projectId}/admin-members/${userId}`
    );
    return response.data.data;
  }
);

// ========== ADMIN TICKET MANAGEMENT ==========

/**
 * Update ticket status (admin only)
 */
export const updateTicketStatus = createAsyncThunk(
  "adminSubscriptions/updateTicketStatus",
  async ({ ticketId, status, notes }) => {
    const response = await api.patch(
      `/admin/subscriptions/projects/tickets/${ticketId}/status`,
      { status, notes }
    );
    return response.data.data;
  }
);

/**
 * Update ticket priority (admin only)
 */
export const updateTicketPriority = createAsyncThunk(
  "adminSubscriptions/updateTicketPriority",
  async ({ ticketId, priority }) => {
    const response = await api.patch(
      `/admin/subscriptions/projects/tickets/${ticketId}/priority`,
      { priority }
    );
    return response.data.data;
  }
);

/**
 * Assign ticket to admin members (admin only)
 */
export const assignTicketToAdmins = createAsyncThunk(
  "adminSubscriptions/assignTicket",
  async ({ ticketId, adminIds }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/tickets/${ticketId}/assign`,
      { adminIds }
    );
    return response.data.data;
  }
);

/**
 * Add comment to ticket (can be nested)
 */
export const addCommentToTicket = createAsyncThunk(
  "subscription/addCommentToTicket",
  async ({
    ticketId,
    content,
    attachments = [],
    isNestedReply = false,
    parentCommentId = null,
  }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/tickets/${ticketId}/comments`,
      { content, attachments, isNestedReply, parentCommentId }
    );
    return response.data;
  }
);

// ===================== PROJECT FILES THUNKS =====================

// Upload files to project
export const uploadProjectFiles = createAsyncThunk(
  "subscription/uploadProjectFiles",
  async ({ projectId, files, note, category }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/${projectId}/files`,
      { files, note, category }
    );
    console.log("Files uploaded:", response.data);
    return response.data;
  }
);

// Get project files
export const getProjectFiles = createAsyncThunk(
  "subscription/getProjectFiles",
  async (projectId) => {
    const response = await api.get(`/admin/subscriptions/projects/${projectId}/files`);
    console.log("Project files fetched:", response.data);
    return response.data;
  }
);

// ========== COMMENTS AND REPLIES THUNKS ==========

/**
 * Add comment to subscription project
 */
export const addCommentsToSubscribedProject = createAsyncThunk(
  "subscription/addCommentsToSubscribedProject",
  async ({ projectId, content, attachments = [] }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/comments`,
      { projectId, content, attachments }
    );
    return response.data;
  }
);

/**
 * Add reply to comment (can be nested)
 */
export const addReplyToProjectComment = createAsyncThunk(
  "subscription/addReplyToProjectComment",
  async ({
    commentId,
    content,
    attachments = [],
    isNestedReply = false,
    parentReplyId = null,
  }) => {
    const response = await api.post(
      `/admin/subscriptions/projects/comments/${commentId}/reply`,
      { content, attachments, isNestedReply, parentReplyId }
    );
    return response.data;
  }
);

/**
 * Get activity logs for a project with filters
 */
export const getProjectActivityLogs = createAsyncThunk(
  "userSubscriptions/getProjectActivityLogs",
  async ({ projectId, filters }) => {
    const query = generateQueryParams(filters);
    const response = await api.get(
      `/admin/subscriptions/projects/${projectId}/activity-logs?${query}`);
    return response.data;
  }
);

/**
 * Get user plan specific notifications
 */
export const getAdminSubscriptionAlerts = createAsyncThunk(
  "subscription/getAdminSubscriptionAlerts",
  async (
    subscriptionId
  ) => {
    console.log('notification subscrption id', subscriptionId)
    const response = await api.get(
      `/admin/subscriptions/alerts/${subscriptionId}`);
    return response.data;
  }
);